import React, { useCallback, useEffect, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";

// WW-173 · chains on the desk (Option A, D89). No chains page: chains open from the review list
// in one column · details on top, programme by day below. Removing an event never deletes it.
const INK = "#1A1A30";
const pill = (on) => ({ border: `2px solid ${INK}`, background: on ? INK : "#FFFFFF", color: on ? "#FFFFFF" : INK });
const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { timeZone: "Africa/Nairobi", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "no date");

const ChainList = ({ state, setState, onOpen, onNew, ticked }) => {
  const [rows, setRows] = useState(null);
  const [newName, setNewName] = useState("");
  useEffect(() => {
    setRows(null);
    client.get(`/admin/chains${state ? `?state=${encodeURIComponent(state)}` : ""}`).then((r) => setRows(r.data.chains)).catch(() => setRows([]));
  }, [state]);
  return (
    <div data-testid="chains-list">
      <div className="flex flex-wrap items-center gap-2">
        {["", "upcoming", "on now", "ended"].map((s) => (
          <button key={s || "all"} type="button" data-testid={`chains-filter-${(s || "all").replace(" ", "-")}`} onClick={() => setState(s)}
                  className="h-8 rounded-full px-3 text-[12px] font-bold lowercase" style={pill(state === s)}>{s || "all chains"}</button>
        ))}
        <button type="button" data-testid="chain-new" onClick={() => setNewName((n) => n || " ")} className="ml-auto h-8 rounded-full px-3 text-[12px] font-bold" style={pill(false)}>+ chain</button>
      </div>
      {(newName || ticked?.length > 0) && (
        <div className="mt-3 flex items-center gap-2" data-testid="chain-new-form">
          <input data-testid="chain-new-name" value={newName.trimStart()} onChange={(e) => setNewName(e.target.value)}
                 placeholder={ticked?.length ? `new chain name for ${ticked.length} ticked events` : "new chain name"}
                 className="h-9 min-w-0 flex-1 rounded-md border px-2 text-[13px]" />
          <button type="button" data-testid="chain-new-save" disabled={!newName.trim()} onClick={() => onNew(newName.trim())}
                  className="h-9 rounded-full px-3 text-[12px] font-bold disabled:opacity-50" style={pill(true)}>make a new chain</button>
        </div>
      )}
      {ticked?.length > 0 && <p className="mt-2 text-[12px]" data-testid="chain-ticked-hint">or add the {ticked.length} ticked events to a chain below</p>}
      <ul className="mt-3 divide-y" style={{ borderColor: "#E4D7C0" }}>
        {rows === null && <li className="py-3 text-[13px]">loading…</li>}
        {rows && !rows.length && <li className="py-3 text-[13px]" data-testid="chains-empty">no chains here yet</li>}
        {(rows || []).map((c) => (
          <li key={c.id} className="flex items-center gap-2">
            <button type="button" data-testid={`chain-row-${c.id}`} onClick={() => onOpen(c.id)} className="flex min-w-0 flex-1 items-center gap-3 py-3 text-left">
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={pill(true)}>chain · {c.event_count}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold">{c.name}</span>
                <span className="block truncate text-[12px] text-neutral-600">{c.status}{c.next_start_at ? ` · next ${when(c.next_start_at)}` : ""}</span>
              </span>
            </button>
            {ticked?.length > 0 && (
              <button type="button" data-testid={`chain-add-ticked-${c.id}`} onClick={() => onOpen(c.id, true)}
                      className="h-8 shrink-0 rounded-full px-3 text-[12px] font-bold" style={pill(true)}>add {ticked.length} here</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const AddEventsDrawer = ({ chainId, onDone, onClose }) => {
  const [data, setData] = useState({ suggested: [], search: [] });
  const [q, setQ] = useState("");
  const [ticked, setTicked] = useState(new Set());
  useEffect(() => {
    const t = setTimeout(() => {
      client.get(`/admin/chains/${chainId}/suggestions?q=${encodeURIComponent(q)}`).then((r) => setData(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [chainId, q]);
  const tick = (id) => setTicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const add = async () => {
    try { await client.post(`/admin/chains/${chainId}/events`, { event_ids: [...ticked] }); toast.success(`added ${ticked.size}`); onDone(); }
    catch { toast.error("could not add"); }
  };
  const Row = ({ e }) => (
    <label className="flex items-center gap-2 py-2 text-[13px]" data-testid={`chain-add-option-${e.id}`}>
      <input type="checkbox" checked={ticked.has(e.id)} onChange={() => tick(e.id)} />
      <span className="min-w-0 flex-1 truncate">{e.title}</span>
      <span className="shrink-0 text-[11px] text-neutral-600">{when(e.start_at)}{e.published ? "" : " · pending"}</span>
    </label>
  );
  return (
    <div data-testid="chain-add-drawer" className="mt-3 rounded-md border p-3" style={{ borderColor: INK }}>
      <div className="flex items-center gap-2">
        <input data-testid="chain-add-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search events by title"
               className="h-9 min-w-0 flex-1 rounded-md border px-2 text-[13px]" />
        <button type="button" data-testid="chain-add-close" onClick={onClose} className="text-[12px] underline">close</button>
      </div>
      <p className="label-tiny mt-3">suggested · same curator, venue, day or name</p>
      {data.suggested.length ? data.suggested.map((e) => <Row key={e.id} e={e} />) : <p className="py-2 text-[12px]">no suggestions</p>}
      {q.trim() && <><p className="label-tiny mt-3">search</p>{data.search.map((e) => <Row key={e.id} e={e} />)}</>}
      <p className="mt-2 text-[12px] text-neutral-600">a new event? add it with + ingest, then tick it here.</p>
      <button type="button" data-testid="chain-add-confirm" disabled={!ticked.size} onClick={add}
              className="mt-2 h-9 rounded-full px-4 text-[12px] font-bold disabled:opacity-50" style={pill(true)}>add {ticked.size} to the chain</button>
    </div>
  );
};

const ChainColumn = ({ id, onBack, onOpenEvent }) => {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [drag, setDrag] = useState(null);
  const load = useCallback(async () => {
    const r = await client.get(`/admin/chains/${id}`);
    setC(r.data);
    setForm({ name: r.data.name, one_line: r.data.one_line || "", cover_image_url: r.data.cover_image_url || "", curator: r.data.curator || "" });
  }, [id]);
  useEffect(() => { load().catch(() => toast.error("could not load the chain")); }, [load]);
  if (!c) return <p className="text-[13px]">loading…</p>;
  const save = async () => { try { await client.put(`/admin/chains/${id}`, form); toast.success("chain saved"); await load(); } catch { toast.error("could not save"); } };
  const order = async (ids) => { try { setC((await client.put(`/admin/chains/${id}/order`, { event_ids: ids })).data); } catch { toast.error("could not reorder"); } };
  const move = (from, to) => { const ids = c.events.map((e) => e.id); const [x] = ids.splice(from, 1); ids.splice(to, 0, x); order(ids); };
  const remove = async (eid) => { try { setC((await client.delete(`/admin/chains/${id}/events/${eid}`)).data); toast.success("taken out of the chain · the event is kept"); } catch { toast.error("could not remove"); } };
  const idx = (eid) => c.events.findIndex((e) => e.id === eid);
  // up/down stay inside a day so the numbers keep reading in order within the programme
  const sameDay = (i, j) => j >= 0 && j < c.events.length && String(c.events[i].start_at || "").slice(0, 10) === String(c.events[j].start_at || "").slice(0, 10);
  return (
    <div data-testid="chain-column">
      <button type="button" data-testid="chain-back" onClick={onBack} className="text-[12px] underline">← chains</button>
      <div className="mt-2 grid gap-2">
        {[["name", "name"], ["one_line", "one line"], ["cover_image_url", "poster (cover) link"], ["curator", "curator"]].map(([k, label]) => (
          <label key={k} className="grid gap-1 text-[12px]"><span className="label-tiny">{label}</span>
            <input data-testid={`chain-field-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 rounded-md border px-2 text-[13px]" />
          </label>
        ))}
        <div className="flex items-center gap-2">
          <button type="button" data-testid="chain-save" onClick={save} className="h-9 rounded-full px-4 text-[12px] font-bold" style={pill(true)}>save</button>
          <span data-testid="chain-status" className="text-[12px]">{c.status} · {c.event_count} events</span>
        </div>
      </div>
      <div className="mt-4 flex items-center"><p className="label-tiny">programme by day</p>
        <button type="button" data-testid="chain-add-events" onClick={() => setAdding(true)} className="ml-auto h-8 rounded-full px-3 text-[12px] font-bold" style={pill(false)}>+ add events</button>
      </div>
      {adding && <AddEventsDrawer chainId={id} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {c.programme.map((d) => (
        <div key={d.day} className="mt-3" data-testid={`chain-day-${d.day}`}>
          <p className="text-[12px] font-bold">{d.day === "no date" ? "no date" : new Date(`${d.day}T12:00:00+03:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
          {d.events.map((e) => (
            <div key={e.id} data-testid={`chain-event-${e.id}`} draggable onDragStart={() => setDrag(e.id)} onDragOver={(ev) => ev.preventDefault()}
                 onDrop={() => { if (drag && drag !== e.id && sameDay(idx(drag), idx(e.id))) move(idx(drag), idx(e.id)); setDrag(null); }}
                 className="mt-1 flex items-center gap-2 rounded-md border px-2 py-2 text-[13px]" style={{ borderColor: "#E4D7C0" }}>
              <span aria-label="drag to reorder" title="drag to reorder" className="cursor-grab select-none">⋮⋮</span>
              <span className="w-6 shrink-0 font-bold">{e.number}</span>
              <span className="min-w-0 flex-1"><span className="block truncate">{e.title}</span>
                <span className="block truncate text-[11px] text-neutral-600">{when(e.start_at)} · {e.venue_display || "no venue"} · {e.price || "no price"}{e.published ? "" : " · pending"}</span></span>
              <button type="button" aria-label="move up" data-testid={`chain-event-up-${e.id}`} disabled={!sameDay(idx(e.id), idx(e.id) - 1)} onClick={() => move(idx(e.id), idx(e.id) - 1)} className="px-1 disabled:opacity-30">↑</button>
              <button type="button" aria-label="move down" data-testid={`chain-event-down-${e.id}`} disabled={!sameDay(idx(e.id), idx(e.id) + 1)} onClick={() => move(idx(e.id), idx(e.id) + 1)} className="px-1 disabled:opacity-30">↓</button>
              <button type="button" data-testid={`chain-event-open-${e.id}`} onClick={() => onOpenEvent(e)} className="text-[12px] underline">open</button>
              <button type="button" data-testid={`chain-event-remove-${e.id}`} onClick={() => remove(e.id)} className="text-[12px] underline" style={{ color: "#9A2222" }}>remove</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const ChainDesk = ({ open, startId, onClose, onOpenEvent, ticked = [], onTickedDone }) => {
  const [state, setState] = useState("");
  const [id, setId] = useState(startId || null);
  useEffect(() => { setId(startId || null); }, [startId, open]);
  if (!open) return null;
  const newChain = async (name) => {
    try {
      setId((await client.post("/admin/chains", { name, event_ids: ticked })).data.id);
      if (ticked.length) onTickedDone && onTickedDone();
    } catch { toast.error("could not make the chain"); }
  };
  const openChain = async (cid, addTicked) => {
    if (addTicked) {
      try { await client.post(`/admin/chains/${cid}/events`, { event_ids: ticked }); toast.success(`added ${ticked.length} to the chain`); onTickedDone && onTickedDone(); }
      catch { toast.error("could not add to the chain"); return; }
    }
    setId(cid);
  };
  return (
    <div data-testid="chain-desk" className="fixed inset-y-0 right-0 z-40 w-full overflow-y-auto border-l bg-[#FDFBF7] p-4 shadow-xl sm:w-[560px]" style={{ borderColor: INK, color: INK }}>
      <div className="flex items-center"><p className="text-[16px] font-bold">chains</p>
        <button type="button" data-testid="chain-desk-close" aria-label="close" onClick={onClose} className="ml-auto text-[18px]">✕</button></div>
      <div className="mt-3">{id ? <ChainColumn id={id} onBack={() => setId(null)} onOpenEvent={(e) => { onOpenEvent(e); onClose(); }} />
        : <ChainList state={state} setState={setState} onOpen={openChain} onNew={newChain} ticked={ticked} />}</div>
    </div>
  );
};

// Event form · "chain" field. Changes apply at once (not part of the draft save).
export const ChainField = ({ eventId, chainId, onChanged }) => {
  const [chains, setChains] = useState([]);
  useEffect(() => { client.get("/admin/chains").then((r) => setChains(r.data.chains)).catch(() => {}); }, []);
  const change = async (v) => {
    try {
      if (!v && chainId) await client.delete(`/admin/chains/${chainId}/events/${eventId}`);
      else if (v) await client.post(`/admin/chains/${v}/events`, { event_ids: [eventId] });
      toast.success(v ? "added to the chain" : "taken out of the chain");
      onChanged && onChanged();
    } catch { toast.error("could not change the chain"); }
  };
  return (
    <label className="mt-3 grid gap-1">
      <span className="label-tiny">chain</span>
      <select data-testid="field-chain" value={chainId || ""} onChange={(e) => change(e.target.value)} className="h-9 rounded-md border px-2 text-[13px]">
        <option value="">not in a chain</option>
        {chains.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </label>
  );
};
